// RESONATE_TRACKING_PIXELS_V1
// Injects Meta Pixel, Google Ads conversion tag, Reddit Pixel, LinkedIn Insight Tag,
// Microsoft Clarity, and the Instantly (Leadsy) de-anonymization pixel into the
// document head. Each pixel is conditionally loaded based on the presence of its
// env var - set them in /app/frontend/.env to activate.
//
// Required env vars (all optional - pixel skipped when missing):
//   REACT_APP_META_PIXEL_ID        e.g. 1234567890123456
//   REACT_APP_GOOGLE_ADS_ID        e.g. AW-1234567890   (no /label here)
//   REACT_APP_REDDIT_PIXEL_ID      e.g. a2_abc123  (defaults to REDDIT_PIXEL_ID below)
//   REACT_APP_LINKEDIN_PARTNER_ID  e.g. 1234567
//   REACT_APP_MS_CLARITY_ID        e.g. abcd1234ef
//   REACT_APP_INSTANTLY_PIXEL_ID   e.g. 4KJW4PfdnG3qolcs  (defaults to INSTANTLY_PIXEL_ID below)
//
// GA4 (G-3ZS92PSRH0) is hard-loaded in index.html.
//
// NOTE ON PIXEL IDS: every .env* file is gitignored repo-wide, so an env var
// never reaches the codespace preview (auto-pull only syncs git) or Vercel.
// Pixel IDs are PUBLIC identifiers - they ship in the client bundle by design,
// exactly like the GA4 ID and PostHog key hardcoded in index.html - so the
// Reddit, Meta, Google Ads and LinkedIn IDs live here as committed defaults.
// Set the matching REACT_APP_*_ID env var to override any of them per env.

import { useEffect } from "react";

// Reddit Ads pixel ID (Resonate Ad Account). Public identifier, safe to commit.
export const REDDIT_PIXEL_ID = "a2_j46ub5eh6k3p";

// Meta (Facebook/Instagram) Pixel ID. Public identifier, safe to commit.
// Automatic Advanced Matching is toggled on in Meta Events Manager (no code
// needed) - it auto-hashes emails/phones typed on the page to lift match rates.
export const META_PIXEL_ID = "1809172370417851";

// Google Ads tag / conversion ID (AW-...). Public identifier, safe to commit.
// Loading + config'ing this tag builds remarketing audiences on page load.
// Firing a specific Google Ads *conversion* additionally needs a conversion
// label (AW-xxx/label) from a conversion action - see trackGoogleAdsConversion.
export const GOOGLE_ADS_ID = "AW-18343229660";

// LinkedIn Insight Tag partner ID. Public identifier, safe to commit. Loading
// the tag builds a LinkedIn retargeting audience on page load (ideal for a B2B
// LinkedIn tool). Tag status flips to Active in Campaign Manager within ~24h of
// the live site receiving traffic.
export const LINKEDIN_PARTNER_ID = "9402834";

// Instantly (Leadsy) de-anonymization pixel ID. Public identifier, safe to
// commit. De-anonymizes site traffic into a company/contact lead feed inside
// Instantly - no visible UI, just a tracking beacon like the pixels above.
export const INSTANTLY_PIXEL_ID = "4KJW4PfdnG3qolcs";

const injected = new Set();

const injectScript = (id, html, attrs = {}) => {
  if (injected.has(id) || document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  s.text = html;
  document.head.appendChild(s);
  injected.add(id);
};

const injectNoscriptImg = (id, src) => {
  if (injected.has(id) || document.getElementById(id)) return;
  const ns = document.createElement("noscript");
  ns.id = id;
  const img = document.createElement("img");
  img.height = "1";
  img.width = "1";
  img.style.display = "none";
  img.src = src;
  ns.appendChild(img);
  document.body.appendChild(ns);
  injected.add(id);
};

const loadMetaPixel = (id) => {
  injectScript(
    "meta-pixel",
    `!function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${id}');
    fbq('track', 'PageView');
    fbq('track', 'ViewContent');`
  );
  injectNoscriptImg(
    "meta-pixel-noscript",
    `https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1`
  );
};

const loadGoogleAds = (id) => {
  // id should be the AW-XXXXXXX conversion ID. gtag.js is already fetched once
  // by the GA4 loader in index.html (the same library file serves every gtag
  // product) - queue this config call through the shared stub instead of
  // fetching a second copy of gtag.js (~140KB + a duplicate parse/eval pass).
  injectScript(
    "google-ads-config",
    `window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag('config', '${id}');`
  );
};

const loadRedditPixel = (id) => {
  injectScript(
    "reddit-pixel",
    `!function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js?pixel_id=${id}",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
    rdt('init','${id}');
    rdt('track', 'PageVisit');`
  );
};

const loadLinkedInInsight = (partnerId) => {
  injectScript(
    "linkedin-insight-config",
    `_linkedin_partner_id = "${partnerId}";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);`
  );
  injectScript(
    "linkedin-insight",
    `(function(l) {
    if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
    window.lintrk.q=[]}
    var s = document.getElementsByTagName("script")[0];
    var b = document.createElement("script");
    b.type = "text/javascript";b.async = true;
    b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    s.parentNode.insertBefore(b, s);})(window.lintrk);`
  );
  injectNoscriptImg(
    "linkedin-insight-noscript",
    `https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif`
  );
};

const loadInstantlyPixel = (pid) => {
  if (injected.has("vtag-ai-js") || document.getElementById("vtag-ai-js")) return;
  const s = document.createElement("script");
  s.id = "vtag-ai-js";
  s.async = true;
  s.src = "https://r2.leadsy.ai/tag.js";
  s.setAttribute("data-pid", pid);
  s.setAttribute("data-version", "062024");
  document.head.appendChild(s);
  injected.add("vtag-ai-js");
};

const loadMsClarity = (id) => {
  injectScript(
    "ms-clarity",
    `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${id}");`
  );
};

// Deferred the same way GA4/PostHog are in index.html: these pixels have no
// above-the-fold rendering job, but loading them eagerly on mount competed
// with LCP paint for main-thread + network (PageSpeed flagged ~240KB of
// render-blocking-adjacent 3rd-party JS here). Idle-callback with an
// interaction fallback means they still fire before any real user action.
export default function TrackingPixels() {
  useEffect(() => {
    const env = process.env;
    const meta = env.REACT_APP_META_PIXEL_ID || META_PIXEL_ID;
    const gAds = env.REACT_APP_GOOGLE_ADS_ID || GOOGLE_ADS_ID;
    const reddit = env.REACT_APP_REDDIT_PIXEL_ID || REDDIT_PIXEL_ID;
    const linkedin = env.REACT_APP_LINKEDIN_PARTNER_ID || LINKEDIN_PARTNER_ID;
    const clarity = env.REACT_APP_MS_CLARITY_ID;
    const instantly = env.REACT_APP_INSTANTLY_PIXEL_ID || INSTANTLY_PIXEL_ID;

    let loaded = false;
    let idleId;
    let delayId;
    // `scroll` is deliberately NOT in this list: it fires within ~1s on mobile,
    // so it dragged the whole pixel payload back into the FCP→TTI window for
    // most real sessions and undid much of the deferral. A deliberate
    // pointer/key/touch gesture still short-circuits the wait, and the idle
    // timeout below guarantees the pixels fire for visitors who never interact
    // at all — so attribution on bounced paid clicks is unaffected.
    const events = ["pointerdown", "keydown", "touchstart"];

    const load = () => {
      if (loaded) return;
      loaded = true;
      if (meta) loadMetaPixel(meta);
      if (gAds) loadGoogleAds(gAds);
      if (reddit) loadRedditPixel(reddit);
      if (linkedin) loadLinkedInInsight(linkedin);
      if (clarity) loadMsClarity(clarity);
      if (instantly) loadInstantlyPixel(instantly);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener("load", whenIdle);
      events.forEach((t) => window.removeEventListener(t, load, { capture: true }));
    };

    // Stagger behind the GA4/PostHog bootstrap in index.html, which uses the
    // same load+idle gate. Without the offset both groups claim the same idle
    // slot and coalesce into one long task — the exact thing deferral is meant
    // to avoid.
    const whenIdle = () => {
      delayId = setTimeout(() => {
        delayId = undefined;
        if ("requestIdleCallback" in window) {
          idleId = requestIdleCallback(load, { timeout: 5000 });
        } else {
          idleId = setTimeout(load, 2500);
        }
      }, 1200);
    };

    if (document.readyState === "complete") whenIdle();
    else window.addEventListener("load", whenIdle, { once: true });
    events.forEach((t) => window.addEventListener(t, load, { passive: true, capture: true, once: true }));

    return () => {
      cleanup();
      if (delayId) clearTimeout(delayId);
      if (idleId) {
        if ("cancelIdleCallback" in window) cancelIdleCallback(idleId);
        else clearTimeout(idleId);
      }
    };
  }, []);

  return null;
}

// Helper: fire a Meta Pixel "Lead" event (call on /thank-you or signup confirmation).
export const trackMetaLead = (value, currency = "USD") => {
  if (typeof window === "undefined" || !window.fbq) return;
  window.fbq("track", "Lead", value ? { value, currency } : undefined);
};

// Helper: fire a Google Ads conversion. conversionLabel is the /xxx part of AW-XXX/yyy.
export const trackGoogleAdsConversion = (conversionLabel, value, currency = "USD") => {
  if (typeof window === "undefined" || !window.gtag) return;
  // Must fall back to the committed default exactly like the loader above —
  // reading only the env var made this a silent no-op in production, where no
  // REACT_APP_GOOGLE_ADS_ID is set (every .env* is gitignored).
  const gAdsId = process.env.REACT_APP_GOOGLE_ADS_ID || GOOGLE_ADS_ID;
  if (!gAdsId || !conversionLabel) return;
  window.gtag("event", "conversion", {
    send_to: `${gAdsId}/${conversionLabel}`,
    value,
    currency,
  });
};

// Generate a unique conversion ID. Reddit uses it to deduplicate a browser
// pixel event against the same event sent via the Conversions API (see the
// "Deduplication" step of Reddit's pixel setup). Any unique string works.
export const newConversionId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// Helper: fire an arbitrary Reddit standard/custom conversion event.
// A conversionId is attached automatically unless one is supplied.
export const trackRedditEvent = (eventName, params = {}) => {
  if (typeof window === "undefined" || !window.rdt) return;
  window.rdt("track", eventName, {
    conversionId: newConversionId(),
    ...params,
  });
};

// Helper: fire a Reddit "Lead" conversion. This is the primary conversion the
// marketing site can observe - a visitor clicking through to sign up (actual
// account creation happens off-domain on app.getresonate.ai). Wired into every
// CTA via trackEvent('cta_click', ...) in src/lib/analytics.js.
export const trackRedditLead = (params = {}) => trackRedditEvent("Lead", params);

// Helper: fire a Reddit "SignUp" conversion (call from a signup-confirmation /
// thank-you surface if one is ever added on-domain).
export const trackRedditSignup = (value, currency = "USD") =>
  trackRedditEvent("SignUp", value ? { value, currency } : {});
